import { useEffect, useState } from 'react';

/**
 * Below this width the format picker's button bar becomes a native select
 * (ADR 0031 §1). The number matches the breakpoint `Settings/Settings.css`
 * already switches at, deliberately: a second nearby threshold would be one
 * more thing to keep in step for no benefit (ADR 0031 §3).
 */
export const NARROW_VIEWPORT = '(max-width: 500px)';

function matches(query: string): boolean {
    // jsdom does not implement matchMedia, so every test that renders a
    // component using this hook would throw. Falling back to false gives the
    // layout that was there before the hook existed, which is the right answer
    // for anything that cannot ask.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }
    return window.matchMedia(query).matches;
}

/**
 * Whether the viewport is narrow enough to want the stacked-control layout.
 *
 * This is a **layout** question and is deliberately not `useCanPreviewPdf`,
 * which answers a *capability* question - will this browser render a blob PDF
 * in an iframe. ADR 0024 exists because those two were conflated once, and a
 * width rule hid the preview from a desktop browser in a narrow window.
 */
export function useNarrowViewport(query: string = NARROW_VIEWPORT): boolean {
    const [narrow, setNarrow] = useState(() => matches(query));

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const list = window.matchMedia(query);
        const onChange = () => setNarrow(list.matches);
        // Read once on mount as well: the first render may have happened
        // before the browser settled on a width.
        onChange();

        // addListener is the deprecated form, kept because Safari only grew
        // addEventListener on MediaQueryList in 14.
        if (typeof list.addEventListener === 'function') {
            list.addEventListener('change', onChange);
            return () => list.removeEventListener('change', onChange);
        }
        if (typeof list.addListener === 'function') {
            list.addListener(onChange);
            return () => list.removeListener(onChange);
        }
        return undefined;
    }, [query]);

    return narrow;
}

export default useNarrowViewport;
